import { supabase } from '@/integrations/supabase/client';
import { cacheImageBytes, getCachedImageObjectUrl, hasCachedImage } from '@/lib/imageCache';

const SIGNED_URL_TTL_SECONDS = 60 * 60;
const CACHE_SAFETY_MARGIN_MS = 5 * 60 * 1000;

const cache = new Map<string, { url: string; expiresAt: number }>();

export interface StorageRef {
  bucket: string;
  path: string;
}

/** Stable cache key for a Storage object, independent of any signing token. */
function storageKey(ref: StorageRef): string {
  return `${ref.bucket}/${ref.path}`;
}

/** Test-only: clears the in-memory signed-URL memo so specs stay isolated. */
export function __clearSignedUrlCache(): void {
  cache.clear();
}

/** Cheap connectivity hint. Cache-first when it says we're offline. */
function isOnlineHint(): boolean {
  try {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  } catch {
    return true;
  }
}

const STORAGE_URL_RE =
  /\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/?#]+)\/([^?#]+)/;

/**
 * Parse a Supabase Storage URL and extract the bucket + object path.
 * Returns null for non-Storage URLs (external, data:, blank).
 */
export function extractStorageRef(url: string | null | undefined): StorageRef | null {
  if (!url) return null;
  const match = STORAGE_URL_RE.exec(url);
  if (!match) return null;
  try {
    return {
      bucket: decodeURIComponent(match[1]),
      path: decodeURIComponent(match[2]),
    };
  } catch {
    return null;
  }
}

/**
 * Turn a stored image URL into something the current session can actually load.
 *
 * - data: URLs → returned as-is (offline placeholders).
 * - Non-Storage URLs → returned as-is (external hotlinks).
 * - Storage URLs → resolved to a short-TTL signed URL. Requires the caller to
 *   have SELECT permission on the object via storage.objects RLS (i.e. it is
 *   in a folder they own). For the anonymous public quote flow, use the
 *   `get-public-quote-media` edge function instead.
 *
 * Signed URLs are cached in-memory until 5 minutes before their expiration.
 */
export async function resolveMediaUrl(url: string | null | undefined): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith('data:')) return url;

  const ref = extractStorageRef(url);
  // Non-Storage URLs (external hotlinks) can't be signed or reliably cached.
  if (!ref) return url;

  const cacheKey = storageKey(ref);

  // Offline: serve the cached bytes immediately instead of a failed round-trip.
  // navigator.onLine can be wrong (captive portals), so if there's no cached
  // copy we still fall through and attempt the network below.
  if (!isOnlineHint()) {
    const offlineUrl = await getCachedImageObjectUrl(cacheKey);
    if (offlineUrl) return offlineUrl;
  }

  // Already-signed URLs pass through: re-signing them just wastes a round-trip
  // and fails outright for anon callers (e.g. PublicQuotePage using URLs the
  // get-public-quote-media edge function already minted). Still warm the bytes
  // so the owner can view them offline later.
  if (url.includes('/object/sign/') && url.includes('token=')) {
    void cacheImageBytes(cacheKey, url);
    return url;
  }

  const cached = cache.get(cacheKey);
  if (cached && cached.expiresAt - CACHE_SAFETY_MARGIN_MS > Date.now()) {
    return cached.url;
  }

  const { data, error } = await supabase.storage
    .from(ref.bucket)
    .createSignedUrl(ref.path, SIGNED_URL_TTL_SECONDS);

  if (error || !data?.signedUrl) {
    // Signing failed (offline, or lost RLS access). Last resort: cached bytes.
    const fallback = await getCachedImageObjectUrl(cacheKey);
    if (fallback) return fallback;
    console.warn('[mediaUrl] Failed to sign', ref, error);
    return null;
  }

  cache.set(cacheKey, {
    url: data.signedUrl,
    expiresAt: Date.now() + SIGNED_URL_TTL_SECONDS * 1000,
  });
  // Warm the bytes in the background so this image survives going offline.
  void cacheImageBytes(cacheKey, data.signedUrl);
  return data.signedUrl;
}

/**
 * Batch-resolve a list of stored image URLs. Preserves order; unresolvable
 * entries become null.
 */
export async function resolveMediaUrls(
  urls: Array<string | null | undefined>
): Promise<Array<string | null>> {
  return Promise.all(urls.map((u) => resolveMediaUrl(u)));
}

async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<unknown>,
): Promise<void> {
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const item = items[cursor++];
      await worker(item);
    }
  });
  await Promise.all(runners);
}

export interface WarmImageBytesResult {
  /** How many images were fetched-and-stored (or attempted) this run. */
  attempted: number;
  /** How many eligible-but-uncached images were skipped by the `max` cap. */
  capped: number;
}

/**
 * Proactively downloads the bytes of a batch of stored images so they render
 * offline even if the user never opened them online. Signs Storage paths in
 * bulk (one request per bucket), skips images already cached, and caps the
 * total so a huge account doesn't trigger a massive background download.
 *
 * Best-effort and side-effect only: it never throws and returns a small report.
 */
export async function warmImageBytesForOffline(
  urls: Array<string | null | undefined>,
  max = 60,
): Promise<WarmImageBytesResult> {
  interface Target {
    key: string;
    bucket: string;
    path: string;
    preSigned: string | null;
    signedUrl?: string;
  }

  const seen = new Set<string>();
  const candidates: Target[] = [];
  for (const url of urls) {
    if (!url || url.startsWith('data:')) continue;
    const ref = extractStorageRef(url);
    if (!ref) continue;
    const key = storageKey(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    const preSigned = url.includes('/object/sign/') && url.includes('token=') ? url : null;
    candidates.push({ key, bucket: ref.bucket, path: ref.path, preSigned });
  }

  // Only download what we don't already have.
  const pending: Target[] = [];
  for (const c of candidates) {
    if (!(await hasCachedImage(c.key))) pending.push(c);
  }

  const capped = Math.max(0, pending.length - max);
  const work = capped > 0 ? pending.slice(0, max) : pending;

  // Batch-sign the public URLs, grouped by bucket.
  const byBucket = new Map<string, Target[]>();
  for (const t of work) {
    if (t.preSigned) continue;
    const list = byBucket.get(t.bucket) || [];
    list.push(t);
    byBucket.set(t.bucket, list);
  }
  for (const [bucket, list] of byBucket) {
    try {
      const { data } = await supabase.storage
        .from(bucket)
        .createSignedUrls(list.map((t) => t.path), SIGNED_URL_TTL_SECONDS);
      (data || []).forEach((entry, i) => {
        if (entry?.signedUrl) list[i].signedUrl = entry.signedUrl;
      });
    } catch (e) {
      console.warn('[mediaUrl] Batch sign failed for bucket', bucket, e);
    }
  }

  const downloads = work
    .map((t) => ({ key: t.key, url: t.preSigned || t.signedUrl }))
    .filter((d): d is { key: string; url: string } => Boolean(d.url));

  await runWithConcurrency(downloads, 4, (d) => cacheImageBytes(d.key, d.url));

  return { attempted: downloads.length, capped };
}
